import { useEffect, useMemo, useState } from "react";
import { useI18n } from "@/lib/i18n";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Sparkles, BookOpen, Briefcase, Cog, Users, Globe } from "lucide-react";

/* Category meta — icon + label. */
const CATEGORIES = [
    { key: "all", label: "Alle Fragen", icon: Sparkles },
    { key: "basics", label: "Grundlagen", icon: BookOpen },
    { key: "business", label: "Strategie", icon: Briefcase },
    { key: "tech", label: "Technik", icon: Cog },
    { key: "collab", label: "Zusammenarbeit", icon: Users },
    { key: "company", label: "Über uns", icon: Globe },
];

export default function FAQ({ faqs = [] }) {
    const { t, pick } = useI18n();
    const [activeCat, setActiveCat] = useState("all");

    // Normalize incoming faqs — each may have { q, a, category? }
    const normalized = useMemo(
        () => faqs.map((f, i) => ({
            id: f.id || `faq-${i}`,
            category: f.category || "basics",
            q: pick(f.q),
            a: pick(f.a),
        })),
        [faqs, pick],
    );

    const visible = useMemo(
        () => activeCat === "all" ? normalized : normalized.filter((f) => f.category === activeCat),
        [normalized, activeCat],
    );

    // JSON-LD FAQPage schema — helps SEO & AI systems (ChatGPT/Perplexity)
    // understand who we are and what we do.
    const jsonLd = useMemo(() => ({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: normalized.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: {
                "@type": "Answer",
                text: f.a,
            },
        })),
    }), [normalized]);

    useEffect(() => {
        if (!normalized.length) return;
        const el = document.createElement("script");
        el.type = "application/ld+json";
        el.text = JSON.stringify(jsonLd);
        el.id = "bm-faq-jsonld";
        // Remove any previous instance (during HMR / route remounts)
        document.getElementById("bm-faq-jsonld")?.remove();
        document.head.appendChild(el);
        return () => el.remove();
    }, [jsonLd, normalized.length]);

    if (!normalized.length) return null;

    return (
        <section
            id="faq"
            data-testid="faq-section"
            className="relative bg-[#0B1120] py-24 sm:py-28 lg:py-32 border-t border-white/10 overflow-hidden"
        >
            {/* subtle ambient glow */}
            <div aria-hidden className="pointer-events-none absolute inset-0 opacity-50"
                 style={{ backgroundImage: "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(16,185,129,0.10) 0%, transparent 60%)" }} />

            <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#10B981]">FAQ · Häufige Fragen</div>
                    <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1]">
                        {t("sections.faq_title")}
                    </h2>
                    <p className="mt-4 text-base text-[#94A3B8] leading-relaxed">
                        Antworten auf die wichtigsten Fragen rund um AI-Lead-Systeme, Funnel-Strategien und unsere
                        Arbeitsweise. Wenn deine Frage nicht dabei ist, schreib uns einfach auf WhatsApp.
                    </p>
                </div>

                {/* Category pills */}
                <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                    {CATEGORIES.map((c) => {
                        const Icon = c.icon;
                        const count = c.key === "all" ? normalized.length : normalized.filter((f) => f.category === c.key).length;
                        if (count === 0 && c.key !== "all") return null;
                        const isActive = activeCat === c.key;
                        return (
                            <button
                                key={c.key}
                                onClick={() => setActiveCat(c.key)}
                                data-testid={`faq-cat-${c.key}`}
                                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all ${
                                    isActive
                                        ? "bg-[#10B981] text-white border-[#10B981] shadow-[0_10px_30px_-12px_rgba(16,185,129,0.55)]"
                                        : "bg-white/[0.03] text-[#CBD5E1] border-white/10 hover:bg-white/[0.06] hover:border-white/20"
                                }`}
                            >
                                <Icon className="size-3.5" />
                                {c.label}
                                <span className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold ${
                                    isActive ? "bg-white/20 text-white" : "bg-white/[0.06] text-[#94A3B8]"
                                }`}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <Accordion
                    type="single"
                    collapsible
                    className="mt-10 space-y-3"
                    data-testid="faq-accordion"
                >
                    {visible.map((f) => (
                        <AccordionItem
                            key={f.id}
                            value={f.id}
                            data-testid={`faq-item-${f.id}`}
                            className="rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur-sm overflow-hidden hover:border-white/20 transition-colors data-[state=open]:border-[#10B981]/30 data-[state=open]:bg-white/[0.04]"
                        >
                            <AccordionTrigger
                                className="px-5 sm:px-6 py-5 text-left font-display text-base sm:text-lg font-semibold text-white hover:no-underline group"
                            >
                                <span className="pr-4">{f.q}</span>
                            </AccordionTrigger>
                            <AccordionContent className="px-5 sm:px-6 pb-6 text-[15px] leading-relaxed text-[#CBD5E1] whitespace-pre-line">
                                {f.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>

                {/* Trust footer line */}
                <div className="mt-12 text-center text-xs text-[#64748B]">
                    BA Media Performance · AI Funnel Lead Systeme · Stockstadt am Main, Deutschland · weltweit tätig
                </div>
            </div>
        </section>
    );
}
